import { IGreetingDialogGroup } from "../../types";

/**
 * 驗證結果
 * @param isValid 是否有效
 * @param duplicates 重複的 key
 */
interface IKeyValidationResult {
    isValid: boolean;
    duplicates: string[];
}

/**
 * 檢查 greetings 中的所有 sentence key 是否全域唯一
 * 根據 API 要求，同一 amaze key 範圍內不可有重複的 key
 * @param greetings 招呼詞組
 * @returns 驗證結果
 */
export const validateUniqueKeys = (greetings: IGreetingDialogGroup[]): IKeyValidationResult => {
    const allKeys = new Map<string, string[]>(); // key -> 出現的位置描述
    const duplicates: string[] = [];

    greetings.forEach((greeting, greetingIndex) => {
        const languageNames = greeting.languages?.join(", ") || "未設定語言";

        greeting.dialogues.forEach((dialogue, dialogueIndex) => {
            dialogue.sentences.forEach((sentence, sentenceIndex) => {
                const key = sentence.key;
                const position = `第${greetingIndex + 1}組(${languageNames})-第${dialogueIndex + 1}個對話-第${
                    sentenceIndex + 1
                }句`;

                if (allKeys.has(key)) {
                    const existingPositions = allKeys.get(key) as string[];
                    existingPositions.push(position);

                    if (!duplicates.includes(key)) {
                        duplicates.push(key);
                    }
                } else {
                    allKeys.set(key, [position]);
                }
            });
        });
    });

    return {
        isValid: duplicates.length === 0,
        duplicates,
    };
};

/**
 * 為重複的 key 生成詳細錯誤訊息
 * @param result 驗證結果
 * @returns 錯誤訊息
 */
export const getDuplicateKeyErrorMessage = (result: IKeyValidationResult): string => {
    if (result.isValid) {
        return "";
    }

    const keyCount = result.duplicates.length;
    const keyList = result.duplicates.join("、");

    return `發現 ${keyCount} 個重複的對話 key：${keyList}。每個 key 在同一角色中必須唯一，請修改後再提交。`;
};

/**
 * 檢查是否有空的 key 值
 * @param greetings 招呼詞組
 * @returns 驗證結果
 */
export const validateNonEmptyKeys = (
    greetings: IGreetingDialogGroup[]
): { isValid: boolean; emptyKeyPositions: string[] } => {
    const emptyKeyPositions: string[] = [];

    greetings.forEach((greeting, greetingIndex) => {
        const languageNames = greeting.languages?.join(", ") || "未設定語言";

        greeting.dialogues.forEach((dialogue, dialogueIndex) => {
            dialogue.sentences.forEach((sentence, sentenceIndex) => {
                if (!sentence.key || sentence.key.trim() === "") {
                    const position = `第${greetingIndex + 1}組(${languageNames})-第${dialogueIndex + 1}個對話-第${
                        sentenceIndex + 1
                    }句`;
                    emptyKeyPositions.push(position);
                }
            });
        });
    });

    return {
        isValid: emptyKeyPositions.length === 0,
        emptyKeyPositions,
    };
};

/**
 * 完整的 greeting 資料驗證
 * @param greetings 招呼詞組
 * @returns 驗證結果
 */
export const validateGreetingData = (greetings: IGreetingDialogGroup[]) => {
    const emptyKeyResult = validateNonEmptyKeys(greetings);
    if (!emptyKeyResult.isValid) {
        return {
            isValid: false,
            errorMessage: `以下位置的對話 key 不可為空：${emptyKeyResult.emptyKeyPositions.join("、")}`,
        };
    }

    const uniqueKeyResult = validateUniqueKeys(greetings);
    if (!uniqueKeyResult.isValid) {
        return {
            isValid: false,
            errorMessage: getDuplicateKeyErrorMessage(uniqueKeyResult),
        };
    }

    return {
        isValid: true,
        errorMessage: "",
    };
};
